// lib/studio-layer-transform.ts
import type { Rect } from '#types/types';

/** Below the browser canvas limit (16384) with margin: a raster layer never exceeds this side. */
export const STUDIO_LAYER_MAX_SIDE_PIXELS = 16000;

export const STUDIO_LAYER_ROTATION_PARAM_KEY = '__studioLayerRotationDeg';
export const STUDIO_LAYER_FLIP_X_PARAM_KEY = '__studioLayerFlipX';
export const STUDIO_LAYER_FLIP_Y_PARAM_KEY = '__studioLayerFlipY';

export type StudioLayerTransform = {
  rotationDeg: number;
  flipX: boolean;
  flipY: boolean;
};

const DEFAULT_TRANSFORM: StudioLayerTransform = {
  rotationDeg: 0,
  flipX: false,
  flipY: false,
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toBoolean = (value: unknown): boolean => {
  if (value === true) return true;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }
  return false;
};

export const normalizeRotationDegrees = (value: unknown): number => {
  const parsed = toFiniteNumber(value) ?? 0;
  const normalized = ((parsed % 360) + 360) % 360;
  const signed = normalized > 180 ? normalized - 360 : normalized;
  return Math.round(signed * 1000) / 1000;
};

export const resolveStudioLayerTransform = (params: unknown): StudioLayerTransform => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return DEFAULT_TRANSFORM;
  }
  const record = params as Record<string, unknown>;
  return {
    rotationDeg: normalizeRotationDegrees(record[STUDIO_LAYER_ROTATION_PARAM_KEY]),
    flipX: toBoolean(record[STUDIO_LAYER_FLIP_X_PARAM_KEY]),
    flipY: toBoolean(record[STUDIO_LAYER_FLIP_Y_PARAM_KEY]),
  };
};

export const mergeStudioLayerTransformParams = (
  params: unknown,
  patch: Partial<StudioLayerTransform>
): Record<string, unknown> => {
  const base =
    params && typeof params === 'object' && !Array.isArray(params)
      ? { ...(params as Record<string, unknown>) }
      : {};
  const current = resolveStudioLayerTransform(base);
  const next = {
    ...current,
    ...patch,
    rotationDeg: normalizeRotationDegrees(patch.rotationDeg ?? current.rotationDeg),
  };
  return {
    ...base,
    [STUDIO_LAYER_ROTATION_PARAM_KEY]: next.rotationDeg,
    [STUDIO_LAYER_FLIP_X_PARAM_KEY]: next.flipX,
    [STUDIO_LAYER_FLIP_Y_PARAM_KEY]: next.flipY,
  };
};

const isDefaultStudioLayerTransform = (transform: StudioLayerTransform): boolean => (
  Math.abs(normalizeRotationDegrees(transform.rotationDeg)) < 0.0005 &&
  !transform.flipX &&
  !transform.flipY
);

type StudioLayerPoint = { x: number; y: number };

const normalizeRect = (rect: Rect): Rect => ({
  x: Number.isFinite(rect.x) ? rect.x : 0,
  y: Number.isFinite(rect.y) ? rect.y : 0,
  w: Math.max(0, Number.isFinite(rect.w) ? rect.w : 0),
  h: Math.max(0, Number.isFinite(rect.h) ? rect.h : 0),
});

const resolveTransformGeometry = (transform: StudioLayerTransform) => {
  const rotationRad = normalizeRotationDegrees(transform.rotationDeg) * Math.PI / 180;
  const cos = Math.cos(rotationRad);
  const sin = Math.sin(rotationRad);
  return {
    cos,
    sin,
    flipX: transform.flipX,
    flipY: transform.flipY,
  };
};

/** Local points use the layer's untransformed top-left as their origin. */
export const projectStudioLayerPointToWorld = (
  point: StudioLayerPoint,
  layerRect: Rect,
  transform: StudioLayerTransform,
): StudioLayerPoint => {
  const frame = normalizeRect(layerRect);
  const geometry = resolveTransformGeometry(transform);
  const localX = (Number.isFinite(point.x) ? point.x : 0) - frame.w / 2;
  const localY = (Number.isFinite(point.y) ? point.y : 0) - frame.h / 2;
  const flippedX = geometry.flipX ? -localX : localX;
  const flippedY = geometry.flipY ? -localY : localY;
  return {
    x: frame.x + frame.w / 2 + flippedX * geometry.cos - flippedY * geometry.sin,
    y: frame.y + frame.h / 2 + flippedX * geometry.sin + flippedY * geometry.cos,
  };
};

export const projectStudioLayerPointToLocal = (
  point: StudioLayerPoint,
  layerRect: Rect,
  transform: StudioLayerTransform,
): StudioLayerPoint => {
  const frame = normalizeRect(layerRect);
  const geometry = resolveTransformGeometry(transform);
  const worldX = (Number.isFinite(point.x) ? point.x : 0) - (frame.x + frame.w / 2);
  const worldY = (Number.isFinite(point.y) ? point.y : 0) - (frame.y + frame.h / 2);
  const rotatedX = worldX * geometry.cos + worldY * geometry.sin;
  const rotatedY = -worldX * geometry.sin + worldY * geometry.cos;
  return {
    x: frame.w / 2 + (geometry.flipX ? -rotatedX : rotatedX),
    y: frame.h / 2 + (geometry.flipY ? -rotatedY : rotatedY),
  };
};

const projectStudioLayerRectCorners = (
  rect: Rect,
  projectPoint: (point: StudioLayerPoint) => StudioLayerPoint,
): Rect => {
  const normalized = normalizeRect(rect);
  const corners = [
    { x: normalized.x, y: normalized.y },
    { x: normalized.x + normalized.w, y: normalized.y },
    { x: normalized.x + normalized.w, y: normalized.y + normalized.h },
    { x: normalized.x, y: normalized.y + normalized.h },
  ].map(projectPoint);
  const minX = Math.min(...corners.map((corner) => corner.x));
  const minY = Math.min(...corners.map((corner) => corner.y));
  const maxX = Math.max(...corners.map((corner) => corner.x));
  const maxY = Math.max(...corners.map((corner) => corner.y));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

export const projectStudioLayerLocalRectToWorld = (
  localRect: Rect,
  layerRect: Rect,
  transform: StudioLayerTransform,
): Rect => projectStudioLayerRectCorners(
  localRect,
  (point) => projectStudioLayerPointToWorld(point, layerRect, transform),
);

export const projectStudioLayerWorldRectToLocal = (
  worldRect: Rect,
  layerRect: Rect,
  transform: StudioLayerTransform,
): Rect => projectStudioLayerRectCorners(
  worldRect,
  (point) => projectStudioLayerPointToLocal(point, layerRect, transform),
);

export const buildStudioLayerRectFromLocalRaster = (
  baseRect: Rect,
  localRect: Pick<Rect, 'x' | 'y'>,
  imageWidth: number,
  imageHeight: number,
  scale: number,
): Rect => ({
  ...baseRect,
  x: baseRect.x + localRect.x,
  y: baseRect.y + localRect.y,
  w: Math.max(0, imageWidth * scale),
  h: Math.max(0, imageHeight * scale),
});

export const buildStudioLayerRectPreservingTransformPivot = (
  baseRect: Rect,
  localRect: Pick<Rect, 'x' | 'y'>,
  imageWidth: number,
  imageHeight: number,
  scale: number,
  originalWorldRect: Rect,
  transform: StudioLayerTransform,
  worldOrigin: Pick<Rect, 'x' | 'y'>,
): Rect => {
  const outputWidth = Math.max(0, imageWidth * scale);
  const outputHeight = Math.max(0, imageHeight * scale);
  const outputCenter = projectStudioLayerPointToWorld(
    {
      x: localRect.x + outputWidth / 2,
      y: localRect.y + outputHeight / 2,
    },
    originalWorldRect,
    transform,
  );
  return {
    ...baseRect,
    x: outputCenter.x - outputWidth / 2 - worldOrigin.x,
    y: outputCenter.y - outputHeight / 2 - worldOrigin.y,
    w: outputWidth,
    h: outputHeight,
  };
};

type StudioLayerTransparentMargins = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

const resolveSoftFrameGuardWeight = (distanceInside: number, guardDepth: number): number => {
  if (guardDepth <= 0) return 255;
  return Math.max(0, Math.min(255, Math.round((1 - distanceInside / guardDepth) * 255)));
};

/** Returns a 0..255 source-alpha weight, with 255 at the frame edge and a soft inner falloff. */
export const buildStudioLayerFrameEdgeGuardMask = ({
  width,
  height,
  layerRect,
  transform,
  frameRect,
  guardBandPx,
  transparentMargins,
}: {
  width: number;
  height: number;
  layerRect: Rect;
  transform: StudioLayerTransform;
  frameRect: Rect;
  guardBandPx: number;
  transparentMargins: StudioLayerTransparentMargins;
}): Uint8Array => {
  const safeWidth = Math.max(0, Math.floor(width));
  const safeHeight = Math.max(0, Math.floor(height));
  const mask = new Uint8Array(safeWidth * safeHeight);
  if (safeWidth === 0 || safeHeight === 0 || layerRect.w <= 0 || layerRect.h <= 0) return mask;

  const pixelWidth = layerRect.w / safeWidth;
  const pixelHeight = layerRect.h / safeHeight;
  const edgeEpsilon = Math.max(pixelWidth, pixelHeight) * 0.5;
  const transformedBounds = getTransformedRectBounds(layerRect, transform);
  const frameLeft = frameRect.x;
  const frameTop = frameRect.y;
  const frameRight = frameRect.x + frameRect.w;
  const frameBottom = frameRect.y + frameRect.h;
  const activeEdges = {
    left: transformedBounds.x <= frameLeft + edgeEpsilon && transformedBounds.x + transformedBounds.w >= frameLeft - edgeEpsilon,
    top: transformedBounds.y <= frameTop + edgeEpsilon && transformedBounds.y + transformedBounds.h >= frameTop - edgeEpsilon,
    right: transformedBounds.x <= frameRight + edgeEpsilon && transformedBounds.x + transformedBounds.w >= frameRight - edgeEpsilon,
    bottom: transformedBounds.y <= frameBottom + edgeEpsilon && transformedBounds.y + transformedBounds.h >= frameBottom - edgeEpsilon,
  };
  const sides = [
    { edge: 'left' as const, rect: { x: 0, y: 0, w: 0, h: layerRect.h }, marginPx: transparentMargins.left, pixelSize: pixelWidth },
    { edge: 'top' as const, rect: { x: 0, y: 0, w: layerRect.w, h: 0 }, marginPx: transparentMargins.top, pixelSize: pixelHeight },
    { edge: 'right' as const, rect: { x: layerRect.w, y: 0, w: 0, h: layerRect.h }, marginPx: transparentMargins.right, pixelSize: pixelWidth },
    { edge: 'bottom' as const, rect: { x: 0, y: layerRect.h, w: layerRect.w, h: 0 }, marginPx: transparentMargins.bottom, pixelSize: pixelHeight },
  ].map((side) => ({
    ...side,
    worldRect: projectStudioLayerLocalRectToWorld(side.rect, layerRect, transform),
  }));
  const resolveEdgeGuard = (edge: 'left' | 'top' | 'right' | 'bottom') => {
    const vertical = edge === 'left' || edge === 'right';
    const edgeCoordinate = edge === 'left' ? frameLeft : edge === 'right' ? frameRight : edge === 'top' ? frameTop : frameBottom;
    const spanStart = vertical ? frameTop : frameLeft;
    const spanEnd = vertical ? frameBottom : frameRight;
    const candidates = sides.filter((side) => {
      const spanStartWorld = vertical ? side.worldRect.y : side.worldRect.x;
      const spanEndWorld = vertical
        ? side.worldRect.y + side.worldRect.h
        : side.worldRect.x + side.worldRect.w;
      return spanEndWorld >= spanStart - edgeEpsilon && spanStartWorld <= spanEnd + edgeEpsilon;
    });
    const closest = candidates.reduce<typeof sides[number] | null>((best, side) => {
      const coordinateStart = vertical ? side.worldRect.x : side.worldRect.y;
      const coordinateEnd = vertical
        ? side.worldRect.x + side.worldRect.w
        : side.worldRect.y + side.worldRect.h;
      const distance = edgeCoordinate < coordinateStart
        ? coordinateStart - edgeCoordinate
        : edgeCoordinate > coordinateEnd
          ? edgeCoordinate - coordinateEnd
          : 0;
      if (!best) return side;
      const bestStart = vertical ? best.worldRect.x : best.worldRect.y;
      const bestEnd = vertical
        ? best.worldRect.x + best.worldRect.w
        : best.worldRect.y + best.worldRect.h;
      const bestDistance = edgeCoordinate < bestStart
        ? bestStart - edgeCoordinate
        : edgeCoordinate > bestEnd
          ? edgeCoordinate - bestEnd
          : 0;
      if (distance < bestDistance - edgeEpsilon) return side;
      if (Math.abs(distance - bestDistance) <= edgeEpsilon) {
        const sideThickness = vertical ? side.worldRect.w : side.worldRect.h;
        const bestThickness = vertical ? best.worldRect.w : best.worldRect.h;
        if (sideThickness < bestThickness) return side;
      }
      return best;
    }, null);
    return closest
      ? (Math.max(0, guardBandPx) + Math.max(0, closest.marginPx)) * closest.pixelSize
      : Math.max(0, guardBandPx) * (vertical ? pixelWidth : pixelHeight);
  };
  const edgeGuards = {
    left: resolveEdgeGuard('left'),
    top: resolveEdgeGuard('top'),
    right: resolveEdgeGuard('right'),
    bottom: resolveEdgeGuard('bottom'),
  };

  for (let y = 0; y < safeHeight; y += 1) {
    for (let x = 0; x < safeWidth; x += 1) {
      const pixelWorldRect = projectStudioLayerLocalRectToWorld(
        { x: x * pixelWidth, y: y * pixelHeight, w: pixelWidth, h: pixelHeight },
        layerRect,
        transform,
      );
      const overlapsFrameVerticalSpan = pixelWorldRect.y + pixelWorldRect.h >= frameTop - edgeEpsilon && pixelWorldRect.y <= frameBottom + edgeEpsilon;
      const overlapsFrameHorizontalSpan = pixelWorldRect.x + pixelWorldRect.w >= frameLeft - edgeEpsilon && pixelWorldRect.x <= frameRight + edgeEpsilon;
      const touchesLeft = activeEdges.left && overlapsFrameVerticalSpan &&
        pixelWorldRect.x <= frameLeft + edgeGuards.left && pixelWorldRect.x + pixelWorldRect.w >= frameLeft - edgeEpsilon;
      const touchesTop = activeEdges.top && overlapsFrameHorizontalSpan &&
        pixelWorldRect.y <= frameTop + edgeGuards.top && pixelWorldRect.y + pixelWorldRect.h >= frameTop - edgeEpsilon;
      const touchesRight = activeEdges.right && overlapsFrameVerticalSpan &&
        pixelWorldRect.x <= frameRight + edgeEpsilon && pixelWorldRect.x + pixelWorldRect.w >= frameRight - edgeGuards.right;
      const touchesBottom = activeEdges.bottom && overlapsFrameHorizontalSpan &&
        pixelWorldRect.y <= frameBottom + edgeEpsilon && pixelWorldRect.y + pixelWorldRect.h >= frameBottom - edgeGuards.bottom;
      let guardWeight = 0;
      if (touchesLeft) {
        const distanceInside = Math.max(0, pixelWorldRect.x - frameLeft);
        guardWeight = Math.max(guardWeight, resolveSoftFrameGuardWeight(distanceInside, edgeGuards.left));
      }
      if (touchesTop) {
        const distanceInside = Math.max(0, pixelWorldRect.y - frameTop);
        guardWeight = Math.max(guardWeight, resolveSoftFrameGuardWeight(distanceInside, edgeGuards.top));
      }
      if (touchesRight) {
        const distanceInside = Math.max(0, frameRight - (pixelWorldRect.x + pixelWorldRect.w));
        guardWeight = Math.max(guardWeight, resolveSoftFrameGuardWeight(distanceInside, edgeGuards.right));
      }
      if (touchesBottom) {
        const distanceInside = Math.max(0, frameBottom - (pixelWorldRect.y + pixelWorldRect.h));
        guardWeight = Math.max(guardWeight, resolveSoftFrameGuardWeight(distanceInside, edgeGuards.bottom));
      }
      mask[y * safeWidth + x] = guardWeight;
    }
  }
  return mask;
};

/** Reprojects an axis-aligned world-space raster into a layer-local raster. */
export const reprojectStudioLayerWorldImageToLocal = (
  sourceImageData: ImageData,
  sourceWorldRect: Rect,
  layerWorldRect: Rect,
  transform: StudioLayerTransform,
  localRect: Rect,
  outputWidth: number,
  outputHeight: number,
): ImageData => {
  const width = Math.max(1, Math.floor(outputWidth));
  const height = Math.max(1, Math.floor(outputHeight));
  const output = new ImageData(width, height);
  if (sourceImageData.width <= 0 || sourceImageData.height <= 0 || sourceWorldRect.w <= 0 || sourceWorldRect.h <= 0) {
    return output;
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const localPoint = {
        x: localRect.x + ((x + 0.5) * localRect.w) / width,
        y: localRect.y + ((y + 0.5) * localRect.h) / height,
      };
      const worldPoint = projectStudioLayerPointToWorld(localPoint, layerWorldRect, transform);
      const sourceX = Math.floor(((worldPoint.x - sourceWorldRect.x) / sourceWorldRect.w) * sourceImageData.width);
      const sourceY = Math.floor(((worldPoint.y - sourceWorldRect.y) / sourceWorldRect.h) * sourceImageData.height);
      if (sourceX < 0 || sourceX >= sourceImageData.width || sourceY < 0 || sourceY >= sourceImageData.height) continue;

      const sourceOffset = (sourceY * sourceImageData.width + sourceX) * 4;
      const outputOffset = (y * width + x) * 4;
      output.data[outputOffset] = sourceImageData.data[sourceOffset] ?? 0;
      output.data[outputOffset + 1] = sourceImageData.data[sourceOffset + 1] ?? 0;
      output.data[outputOffset + 2] = sourceImageData.data[sourceOffset + 2] ?? 0;
      output.data[outputOffset + 3] = sourceImageData.data[sourceOffset + 3] ?? 0;
    }
  }
  return output;
};

export const buildStudioLayerContentCssTransform = (
  transform: StudioLayerTransform
): string | undefined => {
  const transforms: string[] = [];
  const rotation = normalizeRotationDegrees(transform.rotationDeg);
  if (Math.abs(rotation) > 0.0005) {
    transforms.push(`rotate(${rotation}deg)`);
  }
  if (transform.flipX || transform.flipY) {
    transforms.push(`scale(${transform.flipX ? -1 : 1}, ${transform.flipY ? -1 : 1})`);
  }
  return transforms.length > 0 ? transforms.join(' ') : undefined;
};

export const getTransformedRectBounds = (
  rect: Rect,
  transform: StudioLayerTransform
): Rect => {
  const frame = normalizeRect(rect);
  return projectStudioLayerLocalRectToWorld(
    { x: 0, y: 0, w: frame.w, h: frame.h },
    frame,
    transform,
  );
};

export const drawImageWithStudioLayerTransform = (
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  source: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
  transform: StudioLayerTransform,
): void => {
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  context.save();
  context.translate(centerX, centerY);
  const rotation = normalizeRotationDegrees(transform.rotationDeg);
  if (Math.abs(rotation) > 0.0005) {
    context.rotate(rotation * Math.PI / 180);
  }
  context.scale(transform.flipX ? -1 : 1, transform.flipY ? -1 : 1);
  context.drawImage(source, -width / 2, -height / 2, width, height);
  context.restore();
};

const formatSvgNumber = (value: number): string => (
  Number.isFinite(value) ? String(Number(value.toFixed(3))) : '0'
);

export const buildStudioLayerSvgTransform = (
  x: number,
  y: number,
  width: number,
  height: number,
  transform: StudioLayerTransform,
): string => {
  const rotation = normalizeRotationDegrees(transform.rotationDeg);
  if (isDefaultStudioLayerTransform({ ...transform, rotationDeg: rotation })) {
    return `translate(${formatSvgNumber(x)} ${formatSvgNumber(y)})`;
  }
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const parts = [
    `translate(${formatSvgNumber(centerX)} ${formatSvgNumber(centerY)})`,
  ];
  if (Math.abs(rotation) > 0.0005) {
    parts.push(`rotate(${formatSvgNumber(rotation)})`);
  }
  if (transform.flipX || transform.flipY) {
    parts.push(`scale(${transform.flipX ? -1 : 1} ${transform.flipY ? -1 : 1})`);
  }
  parts.push(`translate(${formatSvgNumber(-width / 2)} ${formatSvgNumber(-height / 2)})`);
  return parts.join(' ');
};
